import type { LocalDate } from '@js-joda/core';

export interface EventBase {
    eventId: string;
    exDate: LocalDate;
    symbol: string;
    symbolChangeState?: 'up' | 'down' | 'unchanged';
    type: string;
}

export interface DividendEvent extends EventBase {
    type: 'dividend';
    cashAmount?: number;
    stockAmount?: number;
    ratio?: number;
    ratioDirection?: 'forward' | 'reverse';
    symbolYielded?: string;

    yield?: number;
    dividendType: string;
}

export interface SplitEvent extends EventBase {
    type: 'split';
    ratio?: number;
    reverse: boolean;
    originalPrice?: number;
    newPrice?: number;
}

export interface NewListingEvent extends EventBase {
    type: 'ipo';
    shares?: number;
    price?: number;
}

export interface SymbolChangeEvent extends EventBase {
    type: 'symbol-change';
    oldSymbol: string;
    newSymbol: string;
}

export interface PrimaryExchangeChangeEvent extends EventBase {
    type: 'primary-exchange-change';
    changeType?: string;
    oldExchange?: string;
    newExchange?: string;
}

export interface EarningsEvent extends EventBase {
    type: 'earnings';
    eps?: number;
    revenue?: number;
}

export interface AcquisitionEvent extends EventBase {
    type: 'acquisition';
    acquirerSymbol: string | undefined;
    cashPerShare: number | undefined;
    stockPerShare: number | undefined;
}

export interface BuybackEvent extends EventBase {
    type: 'buyback';
    completionDate: LocalDate;
}

export interface SharesOutstandingChangeEvent extends EventBase {
    type: 'shares-outstanding-change';
    oldShareCount?: number;
    newShareCount: number;
}

export type Event =
    | DividendEvent
    | SplitEvent
    | NewListingEvent
    | SymbolChangeEvent
    | PrimaryExchangeChangeEvent
    | EarningsEvent
    | AcquisitionEvent
    | BuybackEvent
    | SharesOutstandingChangeEvent;
export type EventType = Event['type'];

export const EventTypeGlossary: Record<
    EventType,
    {
        name: string;
        briefName: string;
        color: string;
        symbol: string;
    }
> = {
    dividend: {
        name: 'Dividend',
        briefName: 'Dividend',
        color: 'outline-[#24D995]',
        symbol: 'd',
    },
    split: {
        name: 'Split',
        briefName: 'Split',
        color: 'outline-[#E01818]',
        symbol: 's',
    },
    'primary-exchange-change': {
        name: 'Primary Exchange Change',
        briefName: 'Exchange Δ',
        color: 'outline-[#7f7f7f]',
        symbol: 'Δ',
    },
    'symbol-change': {
        name: 'Symbol Change',
        briefName: 'Symbol Δ',
        color: 'outline-[#FED923]',
        symbol: 'Δ',
    },
    ipo: {
        name: 'New Listing',
        briefName: 'New Listing',
        color: 'outline-[#024A2F]',
        symbol: 'L',
    },
    earnings: {
        name: 'Earnings',
        briefName: 'Earnings',
        color: 'outline-[#124EAF]',
        symbol: 'e',
    },
    acquisition: {
        name: 'Acquisition',
        briefName: 'Acquisition',
        color: 'outline-[#522D67]',
        symbol: 'a',
    },
    buyback: {
        name: 'Buyback',
        briefName: 'Buyback',
        color: 'outline-[#E018D3]',
        symbol: 'b',
    },
    'shares-outstanding-change': {
        name: 'Shares Outstanding Change',
        briefName: 'Shares Δ',
        color: 'outline-[#FF603D]',
        symbol: 'O',
    },
};
Object.freeze(EventTypeGlossary);

export const EventTypes = Object.keys(EventTypeGlossary) as EventType[];
Object.freeze(EventTypes);
